/**
 * Domain Configuration
 *
 * Provides domain-aware support links and branding based on the current hostname.
 * This ensures ReturnCatalyst users see ReturnCatalyst branding, while Corbel users see Corbel branding.
 */

export interface DomainConfig {
  /** Product name to display */
  productName: string;
  /** Company name */
  companyName: string;
  /** Support email address */
  supportEmail: string;
  /** Status page URL (null if no status page) */
  statusPageUrl: string | null;
  /** Documentation URL */
  docsUrl: string;
  /** Whether this is the ReturnCatalyst product */
  isReturnCatalyst: boolean;
  /** Whether this is the Corbel Capital product */
  isCorbelCapital: boolean;
}

/**
 * Get the current domain configuration based on hostname.
 *
 * Detection priority:
 * 1. returncatalyst.ai (we own .ai, NOT .com) → ReturnCatalyst
 * 2. corbelcap.com, corbelcap.ai, corbel-ai-pipeline.web.app → Corbel Capital
 * 3. localhost → defaults to ReturnCatalyst for development
 *
 * @returns Domain configuration for support links and branding
 */
export function getDomainConfig(): DomainConfig {
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';

  // ReturnCatalyst domains (we own .ai, NOT .com)
  if (hostname.includes('returncatalyst.ai')) {
    return {
      productName: 'ReturnCatalyst',
      companyName: 'ReturnCatalyst',
      supportEmail: 'support@returncatalyst.ai',
      statusPageUrl: 'https://status.returncatalyst.ai',
      docsUrl: 'https://docs.returncatalyst.ai',
      isReturnCatalyst: true,
      isCorbelCapital: false,
    };
  }

  // Corbel Capital domains (including Firebase hosting)
  if (
    hostname.includes('corbelcap.com') ||
    hostname.includes('corbelcap.ai') ||
    hostname.includes('corbel-ai-pipeline.web.app') ||
    hostname.includes('corbel-ai-pipeline.firebaseapp.com')
  ) {
    return {
      productName: 'Corbel Capital AI',
      companyName: 'Corbel Capital',
      supportEmail: 'support@corbelcap.com',
      statusPageUrl: 'https://status.corbelcap.com',
      docsUrl: 'https://docs.corbelcap.com',
      isReturnCatalyst: false,
      isCorbelCapital: true,
    };
  }

  // Development/localhost defaults to ReturnCatalyst (the public product)
  return {
    productName: 'ReturnCatalyst',
    companyName: 'ReturnCatalyst',
    supportEmail: 'support@returncatalyst.ai',
    statusPageUrl: 'https://status.returncatalyst.ai',
    docsUrl: 'https://docs.returncatalyst.ai',
    isReturnCatalyst: true,
    isCorbelCapital: false,
  };
}

/**
 * React hook alternative for components that need domain config.
 * Returns the same config as getDomainConfig() but memoized for React usage.
 */
export function useDomainConfig(): DomainConfig {
  // Since hostname doesn't change during runtime, we can compute once
  return getDomainConfig();
}
